import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { FacialVerificationModal } from '../../components/banking/FacialVerificationModal';
import { FacialVerificationModal as AdvancedFacialVerificationModal } from '../../components/facial-verification-advanced';
import { QrCodeScanner } from '../../components/banking/QrCodeScanner';
import { PinRequiredModal } from '../../components/security/PinRequiredModal';
import {
  QrCode,
  Send,
  Clock,
  Copy,
  Check,
  CheckCircle,
  ArrowDownLeft,
  ArrowUpRight,
  X,
  Search,
  Star,
  User,
  Camera,
} from 'lucide-react';

type PixTab = 'send' | 'receive' | 'history';
type PixKeyType = 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'RANDOM';

interface Contact {
  id: string;
  name: string;
  pixKey: string;
  pixKeyType: PixKeyType;
  isFavorite?: boolean;
}

export function CustomerPix() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PixTab>('send');
  const [showQrModal, setShowQrModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Facial verification state
  const [showFacialVerification, setShowFacialVerification] = useState(false);
  const [verificationSessionId, setVerificationSessionId] = useState<string | undefined>();
  const [verificationReason, setVerificationReason] = useState<string | undefined>();
  const [pendingPixData, setPendingPixData] = useState<any>(null);
  const [showQrScanner, setShowQrScanner] = useState(false);

  // Advanced facial verification state (triggered by FACIAL_REQUIRED from security pipeline)
  const [facialRequired, setFacialRequired] = useState(false);
  const [facialModalOpen, setFacialModalOpen] = useState(false);

  // PIN modal state (security-hardening)
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinError, setPinError] = useState<string | undefined>();
  const [pinRemainingAttempts, setPinRemainingAttempts] = useState<number | undefined>();
  
  // Send Pix form state
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState<PixKeyType>('CPF');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  
  // Receive Pix form state
  const [receiveAmount, setReceiveAmount] = useState('');
  const [receiveDescription, setReceiveDescription] = useState('');
  const [generatedQrCode, setGeneratedQrCode] = useState<any>(null);
  const [chargePaymentConfirmed, setChargePaymentConfirmed] = useState(false);

  // Polling for charge payment status
  const { data: chargeStatus } = useQuery({
    queryKey: ['pix', 'charge-status', generatedQrCode?.id],
    queryFn: async () => {
      const response = await api.get(`/banking/pix/charge/${generatedQrCode.id}/status`);
      return response.data;
    },
    enabled: showQrModal && !!generatedQrCode?.id && !chargePaymentConfirmed,
    refetchInterval: 3000,
  });

  // Detect payment confirmation
  useEffect(() => {
    if (chargeStatus?.status === 'COMPLETED') {
      setChargePaymentConfirmed(true);
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['pix', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['statement'] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'transactions'] });
    }
  }, [chargeStatus?.status]);

  // Fetch recent contacts
  const { data: recentContacts } = useQuery({
    queryKey: ['contacts', 'recent'],
    queryFn: async () => {
      try {
        const response = await api.get('/banking/contacts/recent');
        return response.data as Contact[];
      } catch {
        return [];
      }
    },
  });

  // Fetch favorite contacts
  const { data: favoriteContacts } = useQuery({
    queryKey: ['contacts', 'favorites'],
    queryFn: async () => {
      try {
        const response = await api.get('/banking/contacts/favorites');
        return response.data as Contact[];
      } catch {
        return [];
      }
    },
  });

  // Scheduled Pix - DESATIVADO (recurso desabilitado em 03/04/2026)

  // Fetch Pix history
  const { data: pixHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['pix', 'history'],
    queryFn: async () => {
      const response = await api.get('/banking/pix/history');
      return response.data;
    },
    enabled: activeTab === 'history',
  });

  // Validate Pix key mutation
  const validateKeyMutation = useMutation({
    mutationFn: async (data: { key: string; keyType: PixKeyType }) => {
      const response = await api.post('/banking/pix/validate-key', data);
      return response.data;
    },
  });

  // Auto-validate key when user types
  const handlePixKeyChange = (value: string) => {
    setPixKey(value);
    if (value.length > 5) {
      validateKeyMutation.mutate({ key: value, keyType: pixKeyType });
    }
  };

  // Validate pix key format matches selected type
  const getPixKeyValidationError = (): string | null => {
    if (!pixKey || pixKey.length < 3) return null;
    const digits = pixKey.replace(/\D/g, '');

    switch (pixKeyType) {
      case 'CPF':
        if (pixKey.includes('@')) return 'Você selecionou CPF mas digitou um email. Troque o tipo para EMAIL.';
        if (digits.length > 0 && digits.length !== 11) return 'CPF deve ter 11 dígitos.';
        if (digits.length === 14) return 'Isso parece um CNPJ. Troque o tipo para CNPJ.';
        return null;
      case 'CNPJ':
        if (pixKey.includes('@')) return 'Você selecionou CNPJ mas digitou um email. Troque o tipo para EMAIL.';
        if (digits.length > 0 && digits.length !== 14) return 'CNPJ deve ter 14 dígitos.';
        if (digits.length === 11) return 'Isso parece um CPF. Troque o tipo para CPF.';
        return null;
      case 'EMAIL':
        if (!pixKey.includes('@') || !pixKey.includes('.')) return 'Email inválido. Deve conter @ e domínio.';
        if (/^\d+$/.test(digits) && digits.length === 11) return 'Isso parece um CPF. Troque o tipo para CPF.';
        return null;
      case 'PHONE': {
        if (pixKey.includes('@')) return 'Você selecionou Telefone mas digitou um email. Troque o tipo para EMAIL.';
        if (digits.length < 10 || digits.length > 13) return 'Telefone deve ter DDD + número (10 a 13 dígitos).';
        return null;
      }
      case 'RANDOM':
        // UUID format or any string
        return null;
      default:
        return null;
    }
  };

  const pixKeyError = getPixKeyValidationError();

  // Send Pix mutation
  const [sendSuccess, setSendSuccess] = useState<{ amount: string; key: string } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const sendPixMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/pix/send', data);
      return response.data;
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const data = error?.response?.data ?? {};
      const code = data?.code;
      const details = data?.details ?? {};
      const msg = data?.message || error?.message || 'Erro ao enviar PIX';

      // PIN_REQUIRED (HTTP 400) — open PIN modal
      if (status === 400 && code === 'PIN_REQUIRED') {
        setPinError(undefined);
        setPinRemainingAttempts(undefined);
        setPendingPixData((prev: any) => prev ?? {
          pixKey,
          pixKeyType,
          amount: parseAmount(amount),
          description,
        });
        setShowConfirmModal(false);
        setShowPinModal(true);
        return;
      }

      // PIN_INVALID (HTTP 401) — keep modal open, show remaining attempts
      if (status === 401 && code === 'PIN_INVALID') {
        const remaining = typeof details.remainingAttempts === 'number'
          ? details.remainingAttempts
          : undefined;
        setPinRemainingAttempts(remaining);
        setPinError(msg || 'PIN incorreto.');
        setShowPinModal(true);
        return;
      }

      // PIN_LOCKED (HTTP 403) — close modal, show error outside
      if (status === 403 && code === 'PIN_LOCKED') {
        const mins = typeof details.minutesRemaining === 'number'
          ? details.minutesRemaining
          : undefined;
        setShowPinModal(false);
        setPinError(undefined);
        setPinRemainingAttempts(undefined);
        setPendingPixData(null);
        setSendError(
          mins
            ? `PIN bloqueado. Tente novamente em ${mins} minuto(s).`
            : 'PIN bloqueado por excesso de tentativas.',
        );
        setTimeout(() => setSendError(null), 6000);
        return;
      }

      // RATE_LIMITED (HTTP 403) — close modal, show error outside
      if (status === 403 && code === 'RATE_LIMITED') {
        const retry = typeof details.retryAfterSeconds === 'number'
          ? details.retryAfterSeconds
          : undefined;
        setShowPinModal(false);
        setPinError(undefined);
        setPinRemainingAttempts(undefined);
        setPendingPixData(null);
        setSendError(
          retry
            ? `Muitas tentativas. Tente novamente em ${Math.ceil(retry / 60)} minuto(s).`
            : msg,
        );
        setTimeout(() => setSendError(null), 6000);
        return;
      }

      // PASSWORD_RESET_REQUIRED (HTTP 401) — force redirect
      if (status === 401 && code === 'PASSWORD_RESET_REQUIRED') {
        setShowPinModal(false);
        setShowConfirmModal(false);
        setPendingPixData(null);
        navigate('/reset-password-forced');
        return;
      }

      // FACIAL_REQUIRED (HTTP 403) — open advanced FacialVerificationModal
      if (status === 403 && code === 'FACIAL_REQUIRED') {
        setShowPinModal(false);
        setShowConfirmModal(false);
        setPinError(undefined);
        setPinRemainingAttempts(undefined);
        // Preserve the payload so we can retry with verifiedSessionToken
        setPendingPixData((prev: any) => prev ?? {
          pixKey,
          pixKeyType,
          amount: parseAmount(amount),
          description,
        });
        setFacialRequired(true);
        setFacialModalOpen(true);
        return;
      }

      if (msg.includes('Saldo insuficiente') || msg.includes('INSUFFICIENT_BALANCE')) {
        setSendError('Saldo insuficiente para realizar esta transferência.');
      } else if (msg.includes('gateway') || msg.includes('indisponível')) {
        setSendError('Gateway de pagamentos temporariamente indisponível. Tente novamente.');
      } else if (msg.includes('Conta não está ativa')) {
        setSendError('Sua conta não está ativa. Entre em contato com o suporte.');
      } else {
        setSendError(msg);
      }
      setTimeout(() => setSendError(null), 6000);
    },
    onSuccess: (data) => {
      // Check if facial verification is required
      if (data.requiresVerification) {
        setVerificationSessionId(data.sessionId);
        setVerificationReason(data.reason);
        setPendingPixData({
          pixKey,
          pixKeyType,
          amount: parseAmount(amount),
          description,
        });
        setShowConfirmModal(false);
        setShowFacialVerification(true);
        return;
      }

      // Success — clear any PIN state and show confirmation
      const sentAmount = amount;
      const sentKey = pixKey;
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['pix', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['statement'] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'transactions'] });
      setShowConfirmModal(false);
      setShowPinModal(false);
      setPinError(undefined);
      setPinRemainingAttempts(undefined);
      setPendingPixData(null);
      setFacialRequired(false);
      setFacialModalOpen(false);
      setSendSuccess({ amount: sentAmount, key: sentKey });
      resetSendForm();

      // Auto-close success message after 4 seconds
      setTimeout(() => setSendSuccess(null), 4000);
    },
  });

  // Schedule Pix - DESATIVADO (recurso desabilitado em 03/04/2026)

  // Generate QR Code mutation
  const generateQrMutation = useMutation({
    mutationFn: async (data: { amount: number; description?: string }) => {
      const response = await api.post('/banking/pix/generate-qr', data);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.brCode) {
        setGeneratedQrCode(data);
        setChargePaymentConfirmed(false);
        setShowQrModal(true);
      } else {
        alert('QR Code não pôde ser gerado. O provider de pagamentos pode estar indisponível. Tente novamente mais tarde.');
      }
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Erro ao gerar QR Code PIX. Tente novamente.';
      alert(msg);
    },
  });

  // Cancel scheduled - DESATIVADO

  const resetSendForm = () => {
    setPixKey('');
    setAmount('');
    setDescription('');
    setPendingPixData(null);
    setVerificationSessionId(undefined);
    setVerificationReason(undefined);
  };

  const canSend = !!pixKey && !!amount && !pixKeyError && !sendPixMutation.isPending;

  // Handle facial verification completed
  const handleFacialVerificationComplete = async (sessionId: string) => {
    setShowFacialVerification(false);
    
    if (pendingPixData) {
      // Re-send the PIX with the verification session ID
      sendPixMutation.mutate({
        ...pendingPixData,
        verificationSessionId: sessionId,
      });
    }
  };

  // Handle advanced facial verification success — retry PIX with verifiedSessionToken
  const handleAdvancedFacialSuccess = (token: string) => {
    setFacialModalOpen(false);
    setFacialRequired(false);

    if (pendingPixData) {
      sendPixMutation.mutate({
        ...pendingPixData,
        verifiedSessionToken: token,
      });
    }
  };

  // Handle advanced facial verification fallback (camera unavailable / max attempts reached)
  const handleAdvancedFacialFallback = () => {
    setFacialModalOpen(false);
    setFacialRequired(false);
    setPendingPixData(null);
    setSendError('Use PIN + SMS (em breve)');
    setTimeout(() => setSendError(null), 6000);
  };

  const handleAdvancedFacialClose = () => {
    setFacialModalOpen(false);
    setFacialRequired(false);
    setPendingPixData(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const parseAmount = (value: string): number => {
    const cleaned = value.replace(/\D/g, '');
    return parseInt(cleaned, 10) || 0;
  };

  const handleAmountChange = (value: string, setter: (v: string) => void) => {
    const numericValue = value.replace(/\D/g, '');
    const cents = parseInt(numericValue, 10) || 0;
    const formatted = (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setter(formatted);
  };

  const handleSendPix = () => {
    const amountCents = parseAmount(amount);
    sendPixMutation.mutate({
      pixKey,
      pixKeyType,
      amount: amountCents,
      description,
    });
  };

  const handlePinSubmit = (pin: string) => {
    const amountCents = parseAmount(amount);
    const payload = pendingPixData ?? {
      pixKey,
      pixKeyType,
      amount: amountCents,
      description,
    };
    // Keep pendingPixData so we can retry if PIN is rejected
    setPendingPixData(payload);
    sendPixMutation.mutate({ ...payload, pin });
  };

  const handlePinModalClose = () => {
    setShowPinModal(false);
    setPinError(undefined);
    setPinRemainingAttempts(undefined);
    setPendingPixData(null);
  };

  // Handle QR code scanned — parse brCode and fill the send form
  const handleQrScannedForSend = async (decodedText: string) => {
    setShowQrScanner(false);
    try {
      const response = await api.post('/banking/payments/validate-qr', { qrCode: decodedText });
      const parsed = response.data;
      if (parsed.valid && parsed.pixKey) {
        setPixKey(parsed.pixKey);
        // Detect key type
        const key = parsed.pixKey;
        if (/^\d{11}$/.test(key.replace(/\D/g, ''))) setPixKeyType('CPF');
        else if (/^\d{14}$/.test(key.replace(/\D/g, ''))) setPixKeyType('CNPJ');
        else if (key.includes('@')) setPixKeyType('EMAIL');
        else if (/^\+?\d{10,15}$/.test(key.replace(/\D/g, ''))) setPixKeyType('PHONE');
        else setPixKeyType('RANDOM');

        if (parsed.amount) {
          const formatted = (parsed.amount / 100).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          setAmount(formatted);
        }
        if (parsed.description) {
          setDescription(parsed.description);
        }
      }
    } catch {
      // If validation fails, just put the raw code as pixKey
      setPixKey(decodedText);
      setPixKeyType('RANDOM');
    }
  };

  const handleGenerateQr = () => {
    const amountCents = parseAmount(receiveAmount);
    generateQrMutation.mutate({
      amount: amountCents,
      description: receiveDescription,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Select contact to fill form
  const handleSelectContact = (contact: Contact) => {
    setPixKey(contact.pixKey);
    setPixKeyType(contact.pixKeyType);
  };

  const tabs = [
    { id: 'send' as PixTab, label: 'Enviar', icon: <Send size={18} /> },
    { id: 'receive' as PixTab, label: 'Receber', icon: <QrCode size={18} /> },
    { id: 'history' as PixTab, label: 'Histórico', icon: <ArrowUpRight size={18} /> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Pix</h1>
        <p className="text-surface-400">Envie e receba pagamentos instantâneos</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Send Pix Tab */}
      {activeTab === 'send' && (
        <Card>
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white">Enviar Pix</h2>

            {/* Scan QR Code button */}
            <button
              onClick={() => setShowQrScanner(true)}
              className="w-full p-4 rounded-lg border-2 border-dashed border-surface-700 hover:border-primary-500 transition-colors flex items-center justify-center gap-3"
            >
              <Camera size={24} className="text-primary-400" />
              <div className="text-left">
                <p className="text-sm font-medium text-surface-200">Escanear QR Code PIX</p>
                <p className="text-xs text-surface-500">Aponte a câmera para pagar via QR Code</p>
              </div>
            </button>

            {/* Favorite Contacts */}
            {favoriteContacts && favoriteContacts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2 flex items-center gap-2">
                  <Star size={16} className="text-yellow-400" />
                  Favoritos
                </label>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {favoriteContacts.slice(0, 5).map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => handleSelectContact(contact)}
                      className="flex flex-col items-center gap-1 p-3 rounded-lg bg-surface-800 hover:bg-surface-700 border border-surface-700 hover:border-yellow-500/50 transition-all min-w-[80px]"
                    >
                      <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <User size={20} className="text-yellow-400" />
                      </div>
                      <span className="text-xs text-surface-300 truncate max-w-[70px]">
                        {contact.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Contacts */}
            {recentContacts && recentContacts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2 flex items-center gap-2">
                  <Clock size={16} className="text-primary-400" />
                  Recentes
                </label>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {recentContacts.slice(0, 5).map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => handleSelectContact(contact)}
                      className="flex flex-col items-center gap-1 p-3 rounded-lg bg-surface-800 hover:bg-surface-700 border border-surface-700 hover:border-primary-500/50 transition-all min-w-[80px]"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <User size={20} className="text-primary-400" />
                      </div>
                      <span className="text-xs text-surface-300 truncate max-w-[70px]">
                        {contact.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Key Type Selection */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Tipo de chave
              </label>
              <div className="flex flex-wrap gap-2">
                {(['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'RANDOM'] as PixKeyType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setPixKeyType(type)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                      ${pixKeyType === type
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                      }
                    `}
                  >
                    {type === 'RANDOM' ? 'Aleatória' : type}
                  </button>
                ))}
              </div>
            </div>

            {/* Pix Key Input */}
            <Input
              label="Chave Pix"
              placeholder={
                pixKeyType === 'CPF' ? '000.000.000-00' :
                pixKeyType === 'CNPJ' ? '00.000.000/0000-00' :
                pixKeyType === 'EMAIL' ? 'email@exemplo.com' :
                pixKeyType === 'PHONE' ? '11 99999-9999' :
                'Chave aleatória'
              }
              value={pixKey}
              onChange={(e) => handlePixKeyChange(e.target.value)}
              error={pixKeyError || (validateKeyMutation.isError ? 'Chave Pix inválida' : undefined)}
            />

            {/* Amount Input */}
            <Input
              label="Valor"
              placeholder="0,00"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value, setAmount)}
              leftIcon={<span className="text-surface-400">R$</span>}
            />

            {/* Description Input */}
            <Input
              label="Descrição (opcional)"
              placeholder="Ex: Pagamento de serviço"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <div className="flex gap-3">
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => setShowConfirmModal(true)}
                disabled={!canSend}
              >
                Enviar Pix
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Receive Pix Tab */}
      {activeTab === 'receive' && (
        <Card>
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white">Receber Pix</h2>

            <Input
              label="Valor a receber"
              placeholder="0,00"
              value={receiveAmount}
              onChange={(e) => handleAmountChange(e.target.value, setReceiveAmount)}
              leftIcon={<span className="text-surface-400">R$</span>}
            />

            <Input
              label="Descrição (opcional)"
              placeholder="Ex: Pagamento de produto"
              value={receiveDescription}
              onChange={(e) => setReceiveDescription(e.target.value)}
            />

            <Button
              variant="primary"
              className="w-full"
              onClick={handleGenerateQr}
              disabled={!receiveAmount || generateQrMutation.isPending}
            >
              <QrCode size={20} className="mr-2" />
              Gerar QR Code
            </Button>
          </div>
        </Card>
      )}


      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Histórico de Pix</h2>
              <Input
                placeholder="Buscar..."
                className="w-48"
                leftIcon={<Search size={16} className="text-surface-400" />}
              />
            </div>

            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : pixHistory?.items?.length > 0 ? (
              <div className="space-y-2">
                {pixHistory.items.map((pix: any) => (
                  <div
                    key={pix.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${pix.type === 'RECEIVE'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                      }
                    `}>
                      {pix.type === 'RECEIVE'
                        ? <ArrowDownLeft size={20} />
                        : <ArrowUpRight size={20} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {pix.type === 'RECEIVE' ? pix.senderName : pix.recipientName}
                      </p>
                      <p className="text-xs text-surface-400">
                        {formatDate(pix.createdAt)}
                      </p>
                    </div>
                    <span className={`
                      text-sm font-semibold
                      ${pix.type === 'RECEIVE' ? 'text-green-400' : 'text-red-400'}
                    `}>
                      {pix.type === 'RECEIVE' ? '+' : '-'}
                      {formatCurrency(pix.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <QrCode size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum Pix realizado</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confirm Send Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Pix"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-surface-800">
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Chave Pix</span>
              <span className="text-white">{pixKey}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Valor</span>
              <span className="text-white font-semibold">R$ {amount}</span>
            </div>
            {description && (
              <div className="flex justify-between">
                <span className="text-surface-400">Descrição</span>
                <span className="text-white">{description}</span>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleSendPix}
              disabled={sendPixMutation.isPending}
            >
              {sendPixMutation.isPending
                ? 'Processando...'
                : 'Confirmar'
              }
            </Button>
          </div>
        </div>
      </Modal>

      {/* Success Notification */}
      {sendSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
          <div className="bg-green-500/90 backdrop-blur-sm text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Check size={24} />
            </div>
            <div>
              <p className="font-semibold">PIX enviado com sucesso!</p>
              <p className="text-sm text-green-100">R$ {sendSuccess.amount} para {sendSuccess.key}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {sendError && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-red-500/90 backdrop-blur-sm text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 max-w-sm">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <X size={24} />
            </div>
            <div>
              <p className="font-semibold">Erro no envio</p>
              <p className="text-sm text-red-100">{sendError}</p>
            </div>
            <button onClick={() => setSendError(null)} className="ml-2 p-1 hover:bg-white/10 rounded">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      <Modal
        isOpen={showQrModal}
        onClose={() => {
          setShowQrModal(false);
          if (chargePaymentConfirmed) {
            setGeneratedQrCode(null);
            setReceiveAmount('');
            setReceiveDescription('');
            setChargePaymentConfirmed(false);
          }
        }}
        title={chargePaymentConfirmed ? 'Pagamento Confirmado' : 'QR Code Pix'}
      >
        <div className="space-y-4 text-center">
          {chargePaymentConfirmed ? (
            <>
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle size={48} className="text-green-400" />
                </div>
              </div>

              <div className="text-xl font-bold text-green-400">
                Pagamento recebido!
              </div>

              <div className="text-2xl font-bold text-white">
                R$ {receiveAmount}
              </div>

              {chargeStatus?.paidAt && (
                <p className="text-sm text-surface-400">
                  Confirmado em {new Intl.DateTimeFormat('pt-BR', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  }).format(new Date(chargeStatus.paidAt))}
                </p>
              )}

              <Button
                variant="primary"
                className="w-full mt-4"
                onClick={() => {
                  setShowQrModal(false);
                  setGeneratedQrCode(null);
                  setReceiveAmount('');
                  setReceiveDescription('');
                  setChargePaymentConfirmed(false);
                }}
              >
                Fechar
              </Button>
            </>
          ) : generatedQrCode && (
            <>
              {/* Alerta: nome do titular para evitar pagamento cruzado */}
              <div className="p-3 rounded-lg bg-blue-900/30 border border-blue-700">
                <p className="text-xs text-blue-400 font-medium mb-1">Depósito na conta de:</p>
                <p className="text-lg font-bold text-white">{generatedQrCode.accountHolderName || 'Titular da conta'}</p>
                {generatedQrCode.accountHolderCpfPartial && (
                  <p className="text-xs text-blue-300 mt-0.5">CPF: {generatedQrCode.accountHolderCpfPartial}</p>
                )}
              </div>

              <div className="p-4 bg-white rounded-lg inline-block mx-auto">
                <img
                  src={generatedQrCode.qrCodeUrl || generatedQrCode.pixQrCodeUrl || generatedQrCode.qrCodeImage}
                  alt="QR Code Pix"
                  className="w-48 h-48 max-w-full"
                />
              </div>

              <div className="text-2xl font-bold text-white">
                R$ {receiveAmount}
              </div>

              <p className="text-xs text-yellow-400 font-medium">
                Atenção: este QR code é exclusivo para depósito na conta acima. Não compartilhe.
              </p>

              <div className="p-3 rounded-lg bg-surface-800">
                <p className="text-xs text-surface-400 mb-2">Pix Copia e Cola</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={generatedQrCode.brCode}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-white truncate"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedQrCode.brCode)}
                  >
                    {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-surface-400">
                Válido por {generatedQrCode.expiresAt ? (() => { const mins = Math.floor((new Date(generatedQrCode.expiresAt).getTime() - Date.now()) / 60000); return mins > 0 ? `${mins} minutos` : 'expirado'; })() : '24 horas'}
              </p>
            </>
          )}
        </div>
      </Modal>

      {/* Facial Verification Modal */}
      <FacialVerificationModal
        isOpen={showFacialVerification}
        onClose={() => {
          setShowFacialVerification(false);
          setPendingPixData(null);
        }}
        onVerified={handleFacialVerificationComplete}
        operationType="PIX_SEND"
        operationValue={pendingPixData?.amount}
        sessionId={verificationSessionId}
        reason={verificationReason}
      />

      {/* Advanced Facial Verification Modal (triggered by FACIAL_REQUIRED from security pipeline) */}
      <AdvancedFacialVerificationModal
        isOpen={facialModalOpen}
        onClose={handleAdvancedFacialClose}
        onSuccess={handleAdvancedFacialSuccess}
        onFallback={handleAdvancedFacialFallback}
        operationType="PIX_SEND"
      />

      {/* PIN Required Modal (security-hardening) */}
      <PinRequiredModal
        isOpen={showPinModal}
        onClose={handlePinModalClose}
        onSubmit={handlePinSubmit}
        errorMessage={pinError}
        remainingAttempts={pinRemainingAttempts}
        isLoading={sendPixMutation.isPending}
      />

      {/* QR Code Scanner */}
      <QrCodeScanner
        isOpen={showQrScanner}
        onClose={() => setShowQrScanner(false)}
        onScan={handleQrScannedForSend}
      />
    </div>
  );
}

export default CustomerPix;
